import type { AdminFailedPayoutDto, AdminMoneyHealthListDto } from '@carcheck/shared';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { PayoutFailIcon } from './icons';
import { SectionCard } from './SectionCard';
import { SectionMeta } from './SectionMeta';
import { tokens } from '../../../design';
import { formatCurrency, formatDateTime } from '../../../utils/format';
import { failedPayoutsLink } from '../_lib/moneyHealthLinks';
import { countText, truncationText } from '../_lib/moneyHealthText';

/** Number, amount and the rail — the rail verbatim, it is an open set. */
const PayoutHead = ({ payout }: { payout: AdminFailedPayoutDto }): ReactElement => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
    <Typography
      sx={{
        fontFamily: tokens.font.mono,
        fontSize: 13,
        fontWeight: 700,
        color: tokens.text.primary,
      }}
    >
      {`№ ${payout.payoutNumber}`}
    </Typography>
    <Typography
      sx={{
        fontFamily: tokens.font.mono,
        fontSize: 13,
        fontWeight: 700,
        color: tokens.text.primary,
      }}
    >
      {formatCurrency(Number(payout.amount), payout.currency)}
    </Typography>
    <Box
      component="span"
      data-testid="payout-rail"
      sx={{
        ml: 'auto',
        fontFamily: tokens.font.mono,
        fontSize: 10,
        fontWeight: 700,
        px: 1.5,
        py: 0.25,
        borderRadius: `${tokens.radius.pill}px`,
        background: tokens.surface.muted,
        color: tokens.text.secondary,
      }}
    >
      {payout.rail}
    </Box>
  </Box>
);

/** Inspector id, timestamp, wire-decided retry eligibility, and the link out. */
const PayoutMeta = ({ payout }: { payout: AdminFailedPayoutDto }): ReactElement => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        mt: 0.5,
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 2,
        fontSize: 10.5,
        color: tokens.text.tertiary,
      }}
    >
      <Box component="span" sx={{ fontFamily: tokens.font.mono }}>
        {payout.inspectorId}
      </Box>
      <Box component="span">{formatDateTime(payout.createdAt)}</Box>
      <Box
        component="span"
        data-testid="payout-retry-eligibility"
        sx={{
          fontSize: 10,
          fontWeight: 700,
          px: 1.5,
          py: 0.25,
          borderRadius: `${tokens.radius.pill}px`,
          background: payout.retryEligible ? tokens.surface.muted : tokens.state.warningBg,
          color: payout.retryEligible ? tokens.text.secondary : tokens.state.warningStrongText,
        }}
      >
        {payout.retryEligible
          ? t('moneyHealth.payouts.retryEligible')
          : t('moneyHealth.payouts.retryUnavailable')}
      </Box>
      {/*
          A FILTERED payouts list, not `/payouts/:id` — that route does not
          exist (AUT-331) and would dead-end on the catch-all.
        */}
      <RouterLink to={failedPayoutsLink(payout.inspectorId)} style={{ textDecoration: 'none' }}>
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            fontSize: 11,
            fontWeight: 600,
            color: tokens.brand.primary,
          }}
        >
          {t('moneyHealth.payouts.open')}
          <ChevronRightIcon sx={{ fontSize: 14 }} />
        </Box>
      </RouterLink>
    </Box>
  );
};

const PayoutRow = ({ payout }: { payout: AdminFailedPayoutDto }): ReactElement => {
  const { t } = useTranslation();
  return (
    <Box
      data-testid="failed-payout-row"
      sx={{
        px: 3.5,
        py: 1.5,
        borderBottom: `1px solid ${tokens.border.default}`,
        '&:last-of-type': { borderBottom: 0 },
      }}
    >
      <PayoutHead payout={payout} />
      <Typography sx={{ mt: 0.5, fontSize: 11.5, lineHeight: '15px', color: tokens.state.danger }}>
        {/* Vendor free text, verbatim — CONTENT, not user PII. */}
        {payout.failedReason ?? t('moneyHealth.payouts.noReason')}
      </Typography>
      <PayoutMeta payout={payout} />
    </Box>
  );
};

/**
 * «Выплаты с ошибкой» — `status = 'failed'`, which IS "awaiting an operator":
 * a retried payout is flipped to `superseded` and the new attempt is its own
 * row, so this list is exactly the un-actioned ones.
 *
 * **No inspector identity here, and none is fetched.** Name / phone / email
 * would drag the whole `view_pii` reveal convention onto a screen whose job is
 * counting; the footnote says where they live. `rail` and `failedReason` are
 * rendered VERBATIM — they are open sets from vendors, and a Russian lookup
 * table for them renders a blank the day a new rail ships.
 *
 * `retryEligible` comes off the wire, decided by the same predicate the retry
 * endpoint enforces. It is never re-derived from `status` + `rail` here.
 */
export const FailedPayoutsSection = ({
  list,
}: {
  list: AdminMoneyHealthListDto<AdminFailedPayoutDto>;
}): ReactElement => {
  const { t } = useTranslation();
  const count = countText(list);
  const total = t(count.key, { ...count.values });
  const truncation = truncationText(list, total);

  return (
    <SectionCard
      data-testid="section-failed-payouts"
      tone="alert"
      icon={<PayoutFailIcon />}
      title={t('moneyHealth.payouts.title')}
      subtitle={t('moneyHealth.payouts.sub')}
      count={total}
      footer={
        <>
          {truncation === null ? null : <SectionMeta text={truncation} />}
          <SectionMeta text={{ key: 'moneyHealth.payouts.piiNote' }} testId="payouts-pii-note" />
        </>
      }
    >
      {list.items.map((payout) => (
        <PayoutRow key={payout.payoutId} payout={payout} />
      ))}
    </SectionCard>
  );
};
